import { useEffect, useState } from 'react';
import { Option } from '../../../Crud/filters/components/MultiOptionsSelectorFilter/components/MultiOptionsSelector/components/OptionsList/optionsList.types';
import {
  GroupedOptionParams,
  OptionParams,
} from '../components/OptionsList/components/Option/Option';
import { OptionValue } from '../components/OptionsList/optionsList.types';

interface UseOptionsListParams {
  options: Option[];
  canReorder: boolean;
  selectedValues: OptionValue[];
  onSelect: (optionValue: OptionValue | OptionValue[]) => void;
  verifyIfOptionIsVisible: (optionValue: Option) => boolean;
}

const useOptionsList = ({
  canReorder,
  onSelect,
  verifyIfOptionIsVisible,
  selectedValues,
  options,
}: UseOptionsListParams) => {
  const [selectedOptionsToReorder, setSelectedOptionsToReorder] = useState(selectedValues);
  useEffect(() => {
    if (!canReorder) {
      return;
    }
    setSelectedOptionsToReorder(selectedValues);
  }, [canReorder, selectedValues]);

  const isVisible = (option: Option) => {
    const hasChildren = !!option.children?.length;

    if (!hasChildren) {
      return verifyIfOptionIsVisible(option);
    }

    const optionWithChildren = [option, ...option.children!];
    const hasAnyMatchInThisGroup = optionWithChildren.some((opt) => verifyIfOptionIsVisible(opt));
    return hasAnyMatchInThisGroup;
  };

  const getParamsToNotGroupedOption = (option: Option): OptionParams =>
    ({
      isSelected: selectedValues.includes(option.value),
      onSelect,
    }) as OptionParams;
  const getParamsToGroupedOption = (option: Option): GroupedOptionParams => {
    const hasAllSelectedOptions = option.children!.every((childrenOption) =>
      selectedValues.includes(childrenOption.value),
    );
    const hasSelectedOptions = option.children!.some((childrenOption) =>
      selectedValues.includes(childrenOption.value),
    );

    return {
      hasSelectedOptions,
      hasAllSelectedOptions,
      handleSelectAll: () => {
        const childrenValues = option
          .children!.map((childrenOption) => childrenOption.value)
          .concat(option.value);
        if (hasSelectedOptions) {
          const optionsToRemove = selectedValues.filter((value) => childrenValues.includes(value));
          onSelect(optionsToRemove);
          return;
        }
        onSelect(childrenValues);
      },
    } as GroupedOptionParams;
  };

  // TODO: ESSA FUNÇÃO ESTA CAUSANDO RERENDERS NAS OPÇÕES MESMO QUANDO ELAS NÃO SÃO ALTERADAS, ISSO PORQUE O OBJETO QUE ESTAMOS RETORNANDO ESTA SENDO ALTERADO A CADA CHAMADA
  const handleGetParamsToOption = (option: Option): GroupedOptionParams | OptionParams => {
    const hasChildren = !!option.children?.length;

    if (!hasChildren) {
      return getParamsToNotGroupedOption(option);
    }

    return getParamsToGroupedOption(option);
  };

  const validateIfCanAddSelectedClass = (option: Option) => {
    const hasChildren = !!option.children?.length;
    if (!hasChildren) {
      return selectedOptionsToReorder.includes(option.value);
    }

    return selectedOptionsToReorder.some((optionValue) =>
      option.children!.some((childrenOption) => childrenOption.value === optionValue),
    );
  };

  const handleSelectChildren = (optionValue: OptionValue | OptionValue[]) => {
    const values = [optionValue].flat();
    const parentOption = options.find((option) =>
      option.children?.some((child) => values.includes(child.value)),
    );

    if (!parentOption?.value) {
      onSelect(optionValue);
      return;
    }

    const selectedOptionsSet = new Set(selectedValues);
    const selectedChildrenCount = parentOption.children!.filter((child) =>
      selectedOptionsSet.has(child.value),
    ).length;

    const allChildrenSelected =
      values.every((value) => selectedOptionsSet.has(value)) &&
      selectedChildrenCount === values.length;

    const shouldRemoveParentSelection = allChildrenSelected;
    const shouldAddParentSelection = selectedChildrenCount === 0 && !allChildrenSelected;

    if (shouldAddParentSelection || shouldRemoveParentSelection) {
      const valuesWithParentOption = [...values, parentOption.value];
      onSelect(valuesWithParentOption);
      return;
    }

    onSelect(values);
  };

  return {
    isVisible,
    handleSelectChildren,
    handleGetParamsToOption,
    validateIfCanAddSelectedClass,
  };
};

export default useOptionsList;
